import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';

import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'longFormItemTile';

export const OnDemandLongFormItemLayout = {
  TILE_WIDTH: Theme.px(360),
  TILE_HEIGHT: Theme.px(540),
  TILE_SPACING: Theme.px(30),
};

export const OnDemandLongFormItemTile = forwardRef(
  (
    {
      accessibilityLabel,
      accessibilityHint,
      data,
      testID,
      onPress,
      nextFocusUp,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      onFocus,
    },
    reference,
  ) => {
    const { t } = useTranslation('translation', {
      keyPrefix: tPath,
    });

    return (
      <FocusableHighlight
        accessibilityHint={accessibilityHint}
        accessibilityLabel={accessibilityLabel}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        style={styles.container}
        styleFocused={styles.focused}
        testID={testID}
        underlayColor={Theme.withOpacity(Theme.black, 0.6)}
        onFocus={onFocus}
        onPress={onPress}
      >
        <>
          {data?.poster ? (
            <Image resizeMode={'stretch'} source={{ uri: data?.poster }} style={styles.thumbnail} />
          ) : (
            <TWCText noTestID fontFamily={'roboto'} style={styles.errorText}>
              {t('errorMessage')}
            </TWCText>
          )}
        </>
      </FocusableHighlight>
    );
  },
);

OnDemandLongFormItemTile.propTypes = {
  accessibilityLabel: PropTypes.string,
  accessibilityHint: PropTypes.string,
  data: PropTypes.object,
  testID: PropTypes.string,
  onPress: PropTypes.func,
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onFocus: PropTypes.func,
};

OnDemandLongFormItemTile.defaultProps = {
  accessibilityLabel: null,
  accessibilityHint: null,
  data: null,
  testID: null,
  onPress: () => {},
  nextFocusUp: null,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  onFocus: () => {},
};

OnDemandLongFormItemTile.displayName = 'OnDemandLongFormItemTile';

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: Theme.withOpacity(Theme.black, 0.6),
    borderRadius: Theme.tileBorderRadius,
    height: OnDemandLongFormItemLayout.TILE_HEIGHT,
    justifyContent: 'center',
    overflow: 'hidden',
    width: OnDemandLongFormItemLayout.TILE_WIDTH,
  },
  errorText: {
    color: Theme.white,
    fontSize: Theme.px(29),
  },
  focused: {
    borderColor: Theme.white,
    borderRadius: Theme.tileBorderRadius,
    borderWidth: Theme.px(6),
  },
  thumbnail: {
    height: '100%',
    width: '100%',
  },
});
